import React, { useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Box, TextField, Typography, IconButton, Tooltip, Chip, Divider,
  Button, Dialog, DialogTitle, DialogContent, DialogActions,
  InputAdornment, Select, MenuItem, FormControl, InputLabel,
  Alert, CircularProgress, FormControlLabel, Checkbox, Radio, RadioGroup, Switch } from "@mui/material";
import { alpha } from "@mui/material/styles";

import { useColorMode } from "../../../theme/ColorModeContext";
import { ConfirmDialog, EmptyState } from "../../../design-system";
import { useSelector, useDispatch } from "react-redux";
import {
  setLocaleName, setLocaleWelcome, setLocaleWelcomePart, clearLocaleWelcomeParts, setLocaleOfferText, setLanguageOfferEnabled,
  setDLanguage, setLanguages, removeLanguages,
  saveProfileMeta,
} from "../createplan/ProfileSlice";
import { SUPPORTED_CALL_LANGS, MAX_FLOW_LANGS } from "../../../i18n/callFlowLangs.js";
import { previewTTS } from "../createplan/ttsHelpers";
import LanguageOutlinedIcon from "@mui/icons-material/LanguageOutlined";
import { getCallFlow } from "../createplan/SectionsSlice";
import DaysOfWork from "../createplan/DaysOfWork";
// The working week in the screen's language and order; the business's time zone shown and checked (4.3d.4).
import { dayName, sortDayEntries, isOvernight } from "../../../utils/weekdays";
import { tzLabel, browserZone } from "../../../utils/timeZones";
import { setTimezone } from "../createplan/ProfileSlice";
import { Moon, Globe, AlertTriangle } from "lucide-react";
import TlButton from "../../../design-system/components/TlButton";
import { showToast } from "../../../actions/toastSlice";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import { setCredentials } from "../../../actions/authSlice";
import axiosInstance from "../../../services/axiosInstance";
import PasswordRequirements from "../../common/PasswordRequirements";
import { isPasswordValid } from "../../../utils/passwordPolicy";
import VolumeUpOutlinedIcon from "@mui/icons-material/VolumeUpOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import LockOutlinedIcon              from "@mui/icons-material/LockOutlined";
import RecordVoiceOverOutlinedIcon  from "@mui/icons-material/RecordVoiceOverOutlined";
import { ListSubheader }            from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";

const ROW = ({ label, value }) => (
  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", py: 1.25 }}>
    <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 500, minWidth: 130, fontSize: "0.8125rem" }}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ color: "text.primary", fontWeight: 500, textAlign: "right", fontSize: "0.875rem" }}>
      {value || <span style={{ color: "#A9A4BB" }}>-</span>}
    </Typography>
  </Box>
);

const typeColor = (t) =>
  t === "Local"    ? "#4F46E5" :
  t === "TollFree" ? "#06B6D4" : "#E67C00";

// ══════════════════════════════════════════════════════════════════════════════
// PhoneNumbersSection
// ══════════════════════════════════════════════════════════════════════════════
function PhoneNumbersSection() {
  const { t }       = useTranslation("profile");
  const { mode }    = useColorMode();
  const isLight     = mode === "light";
  const dispatch    = useDispatch();
  const userDetails = useSelector((s) => s.auth.userDetails);
  const sections    = useSelector((s) => s.sections.sections);
  const hasFetched  = useSelector((s) => s.sections.hasFetched);

  const [numbers,      setNumbers]      = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [addOpen,      setAddOpen]      = useState(false);
  const [available,    setAvailable]    = useState([]);
  const [loadingAvail, setLoadingAvail] = useState(false);
  const [selected,     setSelected]     = useState([]);
  const [search,       setSearch]       = useState("");
  const [countryF,     setCountryF]     = useState("");
  const [typeF,        setTypeF]        = useState("");
  const [editLabel,    setEditLabel]    = useState(null);
  const [labelValue,   setLabelValue]   = useState("");
  const [removeId,     setRemoveId]     = useState(null);
  const [saving,       setSaving]       = useState(false);
  const [error,        setError]        = useState("");
  const [sectionSaving, setSectionSaving] = useState(null); // numberId being saved

  const load = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get("/PhoneNumbers/my");
      setNumbers(res.data);
    } catch { setError(t("failedLoadNumbers")); }
    finally   { setLoading(false); }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => { if (!hasFetched) dispatch(getCallFlow()); }, [dispatch, hasFetched]);

  const handleAssignSection = async (numberId, sectionName) => {
    setSectionSaving(numberId);
    try {
      await axiosInstance.patch(`/PhoneNumbers/${numberId}/section`, { sectionName: sectionName || null });
      setNumbers((prev) =>
        prev.map((n) => (n.id === numberId ? { ...n, sectionName: sectionName || null } : n))
      );
    } catch { setError(t("failedAssignSection")); }
    finally { setSectionSaving(null); }
  };

  const openAdd = async () => {
    setSelected([]); setSearch(""); setCountryF(""); setTypeF("");
    setAddOpen(true); setLoadingAvail(true);
    try {
      const res = await axiosInstance.get("/PhoneNumbers");
      setAvailable(res.data);
    } catch { setError(t("failedLoadAvailable")); }
    finally   { setLoadingAvail(false); }
  };

  const toggleSelect = (n) =>
    setSelected((prev) =>
      prev.some((s) => s.id === n.id) ? prev.filter((s) => s.id !== n.id) : [...prev, n]
    );

  const handleReserve = async () => {
    if (!selected.length || !userDetails?.id) return;
    setSaving(true); setError("");
    try {
      for (const n of selected)
        await axiosInstance.post(`/PhoneNumbers/${encodeURIComponent(n.id)}/reserve`, {
          countryName: n.countryName,
          dialCode:    n.dialCode,
          numberType:  n.numberType,
          monthlyRate: n.monthlyRate,
        });
      await load();
      setAddOpen(false);
    } catch (e) {
      setError(e.response?.data?.message || t("failedReserveNumber"));
    } finally { setSaving(false); }
  };

  const handleRelease = async () => {
    setSaving(true);
    try {
      await axiosInstance.delete(`/PhoneNumbers/${removeId}/release`);
      setNumbers((prev) => prev.filter((n) => n.id !== removeId));
      setRemoveId(null);
    } catch { setError(t("failedReleaseNumber")); }
    finally { setSaving(false); }
  };

  const handleSaveLabel = async () => {
    setSaving(true);
    try {
      await axiosInstance.patch(`/PhoneNumbers/${editLabel.id}/label`, { label: labelValue });
      setNumbers((prev) =>
        prev.map((n) => (n.id === editLabel.id ? { ...n, label: labelValue } : n))
      );
      setEditLabel(null);
    } catch { setError(t("failedUpdateLabel")); }
    finally { setSaving(false); }
  };

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return available.filter((n) => {
      if (countryF && n.countryName !== countryF) return false;
      if (typeF && n.numberType !== typeF)       return false;
      if (q)
        return (n.number || "").includes(q) ||
               (n.countryName || "").toLowerCase().includes(q) ||
               (n.label || "").toLowerCase().includes(q);
      return true;
    });
  }, [available, search, countryF, typeF]);

  const countries = useMemo(() => [...new Set(available.map((n) => n.countryName).filter(Boolean))], [available]);
  const types     = useMemo(() => [...new Set(available.map((n) => n.numberType).filter(Boolean))],  [available]);

  const glassSx = {
    background: "var(--surface-card)",
    border: "1px solid var(--line-100)",
    boxShadow: "var(--shadow-card)",
  };

  return (
    <Box sx={{ p: 3, borderRadius: "18px", boxShadow: "var(--shadow-card)", mt: 2.5, ...glassSx }}>
      {/* ── section header ── */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              width: 40, height: 40, borderRadius: "12px",
              background: "var(--surface-card)",
               
              border: "1px solid var(--line-100)",
              boxShadow: "none",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}
          >
            <PhoneOutlinedIcon sx={{ fontSize: 20, color: "primary.main" }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 600, fontSize: "0.9375rem", color: "text.primary", lineHeight: 1.2 }}>
              {t("inboundNumbers")}
            </Typography>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              {t("didSubtitle")}
            </Typography>
          </Box>
          {numbers.length > 0 && (
            <Chip
              label={numbers.length}
              size="small"
              sx={{ fontWeight: 700, background: alpha("#4F46E5", 0.1), color: "primary.main", fontSize: "0.75rem" }}
            />
          )}
        </Box>
        <Button
          variant="outlined" size="small"
          startIcon={<AddOutlinedIcon />}
          onClick={openAdd}
          sx={{
            borderRadius: 2, fontWeight: 600, fontSize: "0.8125rem",
            borderColor: alpha("#4F46E5", 0.3), color: "primary.main",
            "&:hover": { background: alpha("#4F46E5", 0.06) },
          }}
        >
          {t("addNumber")}
        </Button>
      </Box>

      <Divider sx={{ mb: 2 }} />

      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
          <CircularProgress size={24} sx={{ color: "primary.main" }} />
        </Box>
      )}

      {!loading && numbers.length === 0 && (
        <EmptyState
          icon={<PhoneOutlinedIcon />}
          title={t("noNumbers")}
          description={t("noNumbersDesc")}
          size="sm"
          inline
        />
      )}

      {/* ── reserved numbers list ── */}
      {!loading &&
        numbers.map((n, idx) => (
          <React.Fragment key={n.id}>
            {idx > 0 && <Divider sx={{ opacity: 0.4 }} />}
            <Box sx={{ py: 1.5, display: "flex", alignItems: "center", gap: 2 }}>
              <Box
                sx={{
                  flexShrink: 0, width: 40, height: 40, borderRadius: "10px",
                  background: alpha("#4F46E5", 0.08),
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}
              >
                <Typography sx={{ fontSize: "0.625rem", fontWeight: 800, color: "primary.main" }}>
                  {n.dialCode || "+?"}
                </Typography>
              </Box>

              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  sx={{ fontWeight: 700, fontSize: "0.9375rem", fontFamily: "monospace", color: "text.primary", letterSpacing: "0.02em" }}
                >
                  {n.number}
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: 0.25, flexWrap: "wrap" }}>
                  <Typography variant="caption" sx={{ color: "text.secondary" }}>{n.countryName}</Typography>
                  <Typography variant="caption" sx={{ color: "text.disabled" }}>·</Typography>
                  <Chip
                    label={n.numberType}
                    size="small"
                    sx={{
                      height: 16, fontSize: "0.625rem", fontWeight: 700,
                      background: alpha(typeColor(n.numberType), 0.1),
                      color: typeColor(n.numberType),
                    }}
                  />
                  <Typography variant="caption" sx={{ color: "text.disabled" }}>·</Typography>
                  <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600 }}>
                    ${n.monthlyRate}/mo
                  </Typography>
                  {n.label && (
                    <>
                      <Typography variant="caption" sx={{ color: "text.disabled" }}>·</Typography>
                      <Chip
                        label={n.label}
                        size="small"
                        sx={{ height: 16, fontSize: "0.625rem", background: alpha("#06B6D4", 0.1), color: "#06B6D4" }}
                      />
                    </>
                  )}
                </Box>
              </Box>

              {/* Section assignment */}
              <Tooltip
                title={sections.length === 0 ? t("noSectionsConfigured") : t("routeToSection")}
                placement="top"
                arrow
              >
                <Box sx={{ flexShrink: 0, minWidth: 150 }}>
                <FormControl size="small" fullWidth>
                  <Select
                    displayEmpty
                    value={n.sectionName || ""}
                    onChange={(e) => handleAssignSection(n.id, e.target.value)}
                    disabled={sectionSaving === n.id || sections.length === 0}
                    renderValue={(v) =>
                      v ? (
                        <Typography sx={{ fontSize: "0.8125rem", fontWeight: 600, color: "#4F46E5" }}>{v}</Typography>
                      ) : (
                        <Typography sx={{ fontSize: "0.8125rem", color: "text.disabled" }}>{t("noSection")}</Typography>
                      )
                    }
                    sx={{
                      borderRadius: "10px",
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: n.sectionName ? alpha("#4F46E5", 0.35) : "divider",
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#4F46E5",
                      },
                      background: n.sectionName ? alpha("#4F46E5", 0.05) : "transparent",
                    }}
                  >
                    <MenuItem value="">
                      <Typography sx={{ fontSize: "0.8125rem", color: "text.disabled" }}>{t("noSection")}</Typography>
                    </MenuItem>
                    {sections.map((s) => (
                      <MenuItem key={s.name} value={s.name}>
                        <Typography sx={{ fontSize: "0.8125rem" }}>{s.name}</Typography>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                </Box>
              </Tooltip>

              <Box sx={{ display: "flex", gap: 0.5, flexShrink: 0 }}>
                <Tooltip title={n.label ? t("editLabel") : t("addLabel")} arrow>
                  <IconButton
                    size="small"
                    onClick={() => { setEditLabel({ id: n.id, label: n.label || "" }); setLabelValue(n.label || ""); }}
                    sx={{ color: "text.disabled", "&:hover": { color: "primary.main" } }}
                  >
                    <EditOutlinedIcon sx={{ fontSize: 17 }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title={t("releaseNumber")} arrow>
                  <IconButton
                    size="small"
                    onClick={() => setRemoveId(n.id)}
                    sx={{ color: "text.disabled", "&:hover": { color: "#E03E3E" } }}
                  >
                    <DeleteOutlineIcon sx={{ fontSize: 17 }} />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          </React.Fragment>
        ))}

      {/* ── Add Number dialog ── */}
      <Dialog
        open={addOpen} onClose={() => setAddOpen(false)}
        maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>{t("addInboundNumber")}</DialogTitle>

        <Box sx={{ px: 3, pb: 1.5, display: "flex", gap: 1.5, flexWrap: "wrap" }}>
          <TextField
            placeholder={t("searchPlaceholder")}
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ flex: 1, minWidth: 180 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchOutlinedIcon sx={{ fontSize: 18, color: "text.disabled" }} />
                </InputAdornment>
              ),
            }}
          />
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel>{t("country")}</InputLabel>
            <Select value={countryF} label={t("country")} onChange={(e) => setCountryF(e.target.value)}>
              <MenuItem value="">{t("all")}</MenuItem>
              {countries.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 110 }}>
            <InputLabel>{t("type", { ns: "common", defaultValue: "Type" })}</InputLabel>
            <Select value={typeF} label={t("type", { ns: "common", defaultValue: "Type" })} onChange={(e) => setTypeF(e.target.value)}>
              <MenuItem value="">{t("all")}</MenuItem>
              {types.map((tp) => <MenuItem key={tp} value={tp}>{tp}</MenuItem>)}
            </Select>
          </FormControl>
        </Box>

        <DialogContent sx={{ pt: 0, maxHeight: 380 }}>
          {loadingAvail && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={24} sx={{ color: "primary.main" }} />
            </Box>
          )}
          {!loadingAvail && filtered.length === 0 && (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography variant="body2" sx={{ color: "text.disabled" }}>{t("noAvailableNumbers")}</Typography>
            </Box>
          )}
          {!loadingAvail &&
            filtered.map((n) => {
              const isSel = selected.some((s) => s.id === n.id);
              return (
                <Box
                  key={n.id}
                  onClick={() => toggleSelect(n)}
                  sx={{
                    p: 1.5, mb: 0.75, borderRadius: 2, cursor: "pointer",
                    border: "1.5px solid",
                    borderColor: isSel ? alpha("#4F46E5", 0.5) : "divider",
                    background: isSel ? alpha("#4F46E5", 0.06) : "background.default",
                    display: "flex", alignItems: "center", gap: 1.5,
                    transition: "all 0.12s",
                    "&:hover": { borderColor: alpha("#4F46E5", 0.35), background: alpha("#4F46E5", 0.04) },
                  }}
                >
                  <Box
                    sx={{
                      width: 34, height: 34, borderRadius: "8px",
                      background: alpha("#4F46E5", 0.08), flexShrink: 0,
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}
                  >
                    <Typography sx={{ fontSize: "0.625rem", fontWeight: 800, color: "primary.main" }}>
                      {n.dialCode}
                    </Typography>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontWeight: 700, fontFamily: "monospace", fontSize: "0.875rem" }}>
                      {n.number}
                    </Typography>
                    <Box sx={{ display: "flex", gap: 0.5, mt: 0.25, alignItems: "center" }}>
                      <Typography variant="caption" sx={{ color: "text.secondary" }}>{n.countryName}</Typography>
                      <Typography variant="caption" sx={{ color: "text.disabled" }}>·</Typography>
                      <Chip
                        label={n.numberType} size="small"
                        sx={{
                          height: 15, fontSize: "0.6rem", fontWeight: 700,
                          background: alpha(typeColor(n.numberType), 0.1),
                          color: typeColor(n.numberType),
                        }}
                      />
                      <Typography variant="caption" sx={{ color: "text.disabled" }}>·</Typography>
                      <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600 }}>
                        ${n.monthlyRate}/mo
                      </Typography>
                    </Box>
                  </Box>
                  {isSel && <CheckCircleIcon sx={{ color: "#4F46E5", fontSize: 20, flexShrink: 0 }} />}
                </Box>
              );
            })}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5, justifyContent: "space-between" }}>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            {selected.length > 0
              ? t("nNumbersSelected", { count: selected.length })
              : t("clickToSelect")}
          </Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button onClick={() => setAddOpen(false)} disabled={saving} sx={{ color: "text.secondary" }}>
              {t("cancel")}
            </Button>
            <Button
              variant="contained"
              onClick={handleReserve}
              disabled={!selected.length || saving}
              startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <AddOutlinedIcon />}
              sx={{ background: "var(--grad-brand-135)", borderRadius: 2 }}
            >
              {`Reserve${selected.length > 0 ? ` (${selected.length})` : ""}`}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* ── Edit Label dialog ── */}
      <Dialog
        open={Boolean(editLabel)} onClose={() => setEditLabel(null)}
        maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>{t("editNumberLabel")}</DialogTitle>
        <DialogContent>
          <TextField
            label={t("label")} fullWidth size="small" autoFocus sx={{ mt: 1 }}
            value={labelValue}
            onChange={(e) => setLabelValue(e.target.value)}
            placeholder={t("labelExPlaceholder")}
            onKeyDown={(e) => e.key === "Enter" && handleSaveLabel()}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setEditLabel(null)} disabled={saving} sx={{ color: "text.secondary" }}>
            {t("cancel")}
          </Button>
          <Button
            variant="contained" onClick={handleSaveLabel} disabled={saving}
            startIcon={saving ? <CircularProgress size={14} color="inherit" /> : null}
            sx={{ background: "var(--grad-brand-135)", borderRadius: 2 }}
          >
            {t("save")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Release confirm dialog ── */}
      <ConfirmDialog
        open={Boolean(removeId)}
        onClose={() => setRemoveId(null)}
        onConfirm={handleRelease}
        title={t("releaseConfirm")}
        description={t("releaseConfirmDesc")}
        confirmLabel={t("release")}
        severity="danger"
        loading={saving}
      />
    </Box>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// SubAccount personal profile
// ══════════════════════════════════════════════════════════════════════════════
function SubAccountProfile() {
  const { t }       = useTranslation("profile");
  const { mode }    = useColorMode();
  const isLight     = mode === "light";
  const dispatch    = useDispatch();
  const userDetails = useSelector((s) => s.auth.userDetails);

  const [newPw,      setNewPw]      = useState("");
  const [confirmPw,  setConfirmPw]  = useState("");
  const [saving,     setSaving]     = useState(false);
  const [pwError,    setPwError]    = useState("");
  const [pwSuccess,  setPwSuccess]  = useState(false);

  const glassSx = {
    background: "var(--surface-card)",
    border: "1px solid var(--line-100)",
    boxShadow: "var(--shadow-card)",
  };

  const handleChangePassword = async () => {
    setPwError(""); setPwSuccess(false);
    if (!newPw || !isPasswordValid(newPw)) { setPwError(t("pwNotMeetReq")); return; }
    if (newPw !== confirmPw)               { setPwError(t("pwNoMatch")); return; }
    setSaving(true);
    try {
      const res = await axiosInstance.post("/SubAccounts/change-password", { newPassword: newPw });
      if (res.data.token) dispatch(setCredentials({ token: res.data.token }));
      setPwSuccess(true);
      setNewPw(""); setConfirmPw("");
    } catch (e) {
      setPwError(e.response?.data?.message || t("failedChangePw"));
    } finally { setSaving(false); }
  };

  return (
    <Box sx={{ maxWidth: 800, mx: "auto" }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, letterSpacing: "-0.02em", color: "text.primary", mb: 0.5 }}>
          {t("myProfile")}
        </Typography>
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          {t("myProfileSubtitle")}
        </Typography>
      </Box>

      {/* personal info card */}
      <Box sx={{ mb: 2.5, p: 3, borderRadius: "18px", boxShadow: "var(--shadow-card)", ...glassSx }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
          <Box
            sx={{
              width: 38, height: 38, borderRadius: "11px",
              background: alpha("#4F46E5", 0.1),
              display: "flex", alignItems: "center", justifyContent: "center",
            }}
          >
            <PersonOutlineOutlinedIcon sx={{ fontSize: 20, color: "primary.main" }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 600, fontSize: "0.9375rem", color: "text.primary", lineHeight: 1.2 }}>
              {t("personalInfo")}
            </Typography>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              {t("managedByAdmin")}
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ mb: 2 }} />

        <ROW label={t("firstName")}  value={userDetails?.firstName} />
        <Divider sx={{ opacity: 0.4 }} />
        <ROW label={t("lastName")}   value={userDetails?.lastName} />
        <Divider sx={{ opacity: 0.4 }} />
        <ROW label={t("email")}       value={userDetails?.email} />
        <Divider sx={{ opacity: 0.4 }} />
        <ROW label={t("phone")}       value={userDetails?.mobileNumber} />
        <Divider sx={{ opacity: 0.4 }} />
        <ROW label={t("extension")}   value={userDetails?.extension} />
        <Divider sx={{ opacity: 0.4 }} />
        <ROW label={t("section")}     value={userDetails?.sectionName} />
        <Divider sx={{ opacity: 0.4 }} />
        <ROW label={t("jobTitle")}   value={userDetails?.jobTitle} />
      </Box>

      {/* change password card */}
      <Box sx={{ p: 3, borderRadius: "18px", boxShadow: "var(--shadow-card)", ...glassSx }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
          <Box
            sx={{
              width: 38, height: 38, borderRadius: "11px",
              background: alpha("#06B6D4", 0.1),
              display: "flex", alignItems: "center", justifyContent: "center",
            }}
          >
            <InfoOutlinedIcon sx={{ fontSize: 20, color: "#06B6D4" }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 600, fontSize: "0.9375rem", color: "text.primary", lineHeight: 1.2 }}>
              {t("changePassword")}
            </Typography>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              {t("pwRequirements")}
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ mb: 2.5 }} />

        {pwError && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setPwError("")}>
            {pwError}
          </Alert>
        )}
        {pwSuccess && (
          <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setPwSuccess(false)}>
            {t("pwSuccess")}
          </Alert>
        )}

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Box>
            <TextField
              label={t("newPassword")}
              type="password"
              fullWidth
              size="medium"
              value={newPw}
              onChange={(e) => setNewPw(e.target.value)}
            />
            <PasswordRequirements password={newPw} />
          </Box>
          <TextField
            label={t("confirmNewPassword")}
            type="password"
            fullWidth
            size="medium"
            value={confirmPw}
            onChange={(e) => setConfirmPw(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleChangePassword()}
          />
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              variant="contained"
              onClick={handleChangePassword}
              disabled={saving || !newPw || !confirmPw}
              startIcon={saving ? <CircularProgress size={14} color="inherit" /> : null}
              sx={{ background: "var(--grad-brand-135)", borderRadius: 2, fontWeight: 600 }}
            >
              {saving ? t("saving") : t("changePassword")}
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// Profile page
// ══════════════════════════════════════════════════════════════════════════════
function ChangePasswordCard({ glassSx }) {
  const { t }     = useTranslation("profile");
  const [form,    setForm]    = useState({ current: '', next: '', confirm: '' });
  const [show,    setShow]    = useState({ current: false, next: false, confirm: false });
  const [saving,  setSaving]  = useState(false);
  const [success, setSuccess] = useState(false);
  const [err,     setErr]     = useState('');

  const toggle = (field) => setShow((s) => ({ ...s, [field]: !s[field] }));
  const set    = (field, val) => { setForm((f) => ({ ...f, [field]: val })); setSuccess(false); setErr(''); };

  const handleSave = async () => {
    if (!form.current || !form.next || !form.confirm) { setErr(t('allFieldsRequired')); return; }
    if (!isPasswordValid(form.next)) { setErr(t('newPwNotMeetReq')); return; }
    if (form.next !== form.confirm) { setErr(t('newPwNoMatch')); return; }
    setSaving(true); setErr('');
    try {
      await axiosInstance.post('/User/change-password', { currentPassword: form.current, newPassword: form.next });
      setSuccess(true);
      setForm({ current: '', next: '', confirm: '' });
    } catch (e) {
      setErr(e.response?.data?.message || t('failedUpdatePw'));
    } finally { setSaving(false); }
  };

  const FieldAdornment = ({ field }) => (
    <InputAdornment position='end'>
      <IconButton size='small' onClick={() => toggle(field)} edge='end'>
        {show[field]
          ? <VisibilityOffOutlinedIcon sx={{ fontSize: 18 }} />
          : <VisibilityOutlinedIcon   sx={{ fontSize: 18 }} />}
      </IconButton>
    </InputAdornment>
  );

  return (
    <Box sx={{ mt: 2.5, p: 3, borderRadius: '18px', ...glassSx }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
        <Box sx={{ width: 38, height: 38, borderRadius: '11px', background: alpha('#EF4444', 0.1), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <LockOutlinedIcon sx={{ fontSize: 20, color: '#EF4444' }} />
        </Box>
        <Box>
          <Typography sx={{ fontWeight: 600, fontSize: '0.9375rem', color: 'text.primary', lineHeight: 1.2 }}>
            {t('security')}
          </Typography>
          <Typography variant='caption' sx={{ color: 'text.secondary' }}>
            {t('changeAccountPw')}
          </Typography>
        </Box>
      </Box>

      <Divider sx={{ mb: 2.5 }} />

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 420 }}>
        <TextField
          label={t('currentPassword')} size='medium' fullWidth
          type={show.current ? 'text' : 'password'}
          value={form.current}
          onChange={(e) => set('current', e.target.value)}
          InputProps={{ endAdornment: <FieldAdornment field='current' /> }}
        />
        <Box>
          <TextField
            label={t('newPassword')} size='medium' fullWidth
            type={show.next ? 'text' : 'password'}
            value={form.next}
            onChange={(e) => set('next', e.target.value)}
            InputProps={{ endAdornment: <FieldAdornment field='next' /> }}
          />
          <PasswordRequirements password={form.next} />
        </Box>
        <TextField
          label={t('confirmNewPassword')} size='medium' fullWidth
          type={show.confirm ? 'text' : 'password'}
          value={form.confirm}
          onChange={(e) => set('confirm', e.target.value)}
          InputProps={{ endAdornment: <FieldAdornment field='confirm' /> }}
        />

        {err     && <Alert severity='error'   sx={{ py: 0.5 }}>{err}</Alert>}
        {success && <Alert severity='success' sx={{ py: 0.5 }}>{t('pwUpdatedSuccess')}</Alert>}

        <Box>
          <Button
            variant='contained' onClick={handleSave} disabled={saving}
            sx={{ fontWeight: 700, background: 'var(--grad-brand-135)', '&:hover': { filter: 'brightness(1.06)', background: 'var(--grad-brand-135)' } }}
          >
            {saving ? t('saving') : t('updatePassword')}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// LanguageIdentityCard — mirrors the language/welcome panel from the cards view
// ══════════════════════════════════════════════════════════════════════════════
function LanguageIdentityCard({ glassSx }) {
  const { t }    = useTranslation("profile");
  const dispatch = useDispatch();
  const profile  = useSelector((s) => s.profile.profile);

  const [saving, setSaving] = useState(false);
  const [saved,  setSaved]  = useState(false);
  const [err,    setErr]    = useState("");

  const handleLangCheck = (e) => {
    if (e.target.checked) {
      dispatch(setLanguages({ value: e.target.value }));
    } else {
      if (profile.languages.length > 1) {
        const remaining = profile.languages.filter((l) => l !== e.target.value);
        dispatch(setDLanguage({ value: remaining[0] }));
        dispatch(removeLanguages({ value: e.target.value }));
      }
    }
  };

  const handleDefaultLang = (e) => {
    dispatch(setDLanguage({ value: e.target.value }));
  };

  const handleSave = async () => {
    setSaving(true); setSaved(false); setErr("");
    try {
      await dispatch(saveProfileMeta()).unwrap();
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch {
      setErr(t("failedSave"));
    } finally {
      setSaving(false);
    }
  };

  const dLang = typeof profile.dLanguage === "string"
    ? profile.dLanguage
    : (profile.languages[0] || "en");

  return (
    <Box sx={{ mt: 2.5, p: 3, borderRadius: "18px", ...glassSx }}>
      {/* header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
        <Box sx={{ width: 38, height: 38, borderRadius: "11px", background: alpha("#4F46E5", 0.1), display: "flex", alignItems: "center", justifyContent: "center" }}>
          <LanguageOutlinedIcon sx={{ fontSize: 20, color: "primary.main" }} />
        </Box>
        <Box>
          <Typography sx={{ fontWeight: 600, fontSize: "0.9375rem", color: "text.primary", lineHeight: 1.2 }}>
            {t("langVoiceIdentity")}
          </Typography>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            {t("ivrLangOptions")}
          </Typography>
        </Box>
      </Box>

      <Divider sx={{ mb: 2.5 }} />

      {/* Language selection — all supported languages */}
      <Typography variant="caption" sx={{ display: "block", mb: 1, color: "text.disabled", textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 700, fontSize: "0.6875rem" }}>
        {t("enabledLanguages")}
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1, mb: 1 }}>
        {SUPPORTED_CALL_LANGS.map(({ code, nativeName }) => {
          const on = profile.languages.includes(code);
          const full = !on && profile.languages.length >= MAX_FLOW_LANGS;
          const last = on && profile.languages.length <= 1;
          return (
            <Tooltip key={code} title={full ? t("langCapHint", { max: MAX_FLOW_LANGS }) : last ? t("langLastHint") : ""} arrow>
              <span>
                <FormControlLabel
                  value={code}
                  control={<Checkbox checked={on} disabled={full || last} onChange={handleLangCheck} />}
                  label={nativeName}
                />
              </span>
            </Tooltip>
          );
        })}
      </Box>
      <Typography variant="caption" sx={{ display: "block", mb: 2.5, color: "text.secondary" }}>
        {t("langCap", { count: profile.languages.length, max: MAX_FLOW_LANGS })}
      </Typography>

      {/* Default language — only enabled ones */}
      <Typography variant="caption" sx={{ display: "block", mb: 1, color: "text.disabled", textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 700, fontSize: "0.6875rem" }}>
        {t("defaultLanguage")}
      </Typography>
      <RadioGroup row value={dLang} onChange={handleDefaultLang} sx={{ mb: 2.5 }}>
        {SUPPORTED_CALL_LANGS.map(({ code, nativeName }) => (
          <FormControlLabel
            key={code}
            value={code}
            control={<Radio />}
            label={nativeName}
            disabled={!profile.languages.includes(code)}
          />
        ))}
      </RadioGroup>

      <Divider sx={{ mb: 2.5 }} />

      {/* Welcome messages — one field per enabled language */}
      <Typography variant="caption" sx={{ display: "block", mb: 1.5, color: "text.disabled", textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 700, fontSize: "0.6875rem" }}>
        {t("welcomeMessages")}
      </Typography>

      {SUPPORTED_CALL_LANGS.filter(({ code }) => profile.languages.includes(code)).map(({ code, label, dir }) => {
        const val = profile.locales?.[code]?.welcomeMessage || "";
        const wp = profile.locales?.[code]?.welcomeParts || {};
        const split = !!(wp.thanks || wp.positioning || wp.invitation);
        const PARTS = [
          { key: "thanks",      label: t("partThanks"),      help: t("partThanksHelp") },
          { key: "positioning", label: t("partPositioning"), help: t("partPositioningHelp") },
          { key: "invitation",  label: t("partInvitation"),  help: t("partInvitationHelp") },
        ];
        return (
          <Box key={code} sx={{ mb: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
              <TextField
                fullWidth
                label={`${t("welcomeMessageLabel")} (${label})`}
                value={val}
                onChange={(e) => dispatch(setLocaleWelcome({ lang: code, value: e.target.value }))}
                size="medium"
                inputProps={{ dir }}
                disabled={split}
                helperText={split ? t("singleIgnored") : ""}
              />
              <Tooltip title={t("previewPronunciation")} arrow>
                <span>
                  <IconButton
                    onClick={() => previewTTS(val, `welcome-${code}`)}
                    disabled={!val.trim() || split}
                    sx={{ flexShrink: 0, color: "text.secondary", transition: "transform 0.8s ease, color 0.15s", "&:hover": { transform: "rotate(360deg)", color: "primary.main" } }}
                  >
                    <VolumeUpOutlinedIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>

            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={split}
                  onChange={(e) => {
                    if (e.target.checked) dispatch(setLocaleWelcomePart({ lang: code, part: "invitation", value: val || " " }));
                    else dispatch(clearLocaleWelcomeParts({ lang: code }));
                  }}
                />
              }
              label={
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  {t("splitWelcome")}
                </Typography>
              }
              sx={{ ml: 0 }}
            />

            {split && (
              <Box sx={{ mt: 1, pl: { xs: 0, sm: 2 }, borderInlineStart: { sm: "2px solid var(--line-200)" }, display: "flex", flexDirection: "column", gap: 2 }}>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>{t("splitWelcomeHelp")}</Typography>
                {PARTS.map(({ key, label: pl, help }) => {
                  const pv = wp[key] || "";
                  return (
                    <Box key={key} sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                      <TextField
                        fullWidth size="small" label={pl} value={pv} helperText={help}
                        onChange={(e) => dispatch(setLocaleWelcomePart({ lang: code, part: key, value: e.target.value }))}
                        inputProps={{ dir }}
                      />
                      <Tooltip title={t("previewPronunciation")} arrow>
                        <span>
                          <IconButton
                            onClick={() => previewTTS(pv, `welcome-${code}-${key}`)}
                            disabled={!pv.trim()}
                            sx={{ flexShrink: 0, mt: 0.5, color: "text.secondary", transition: "transform 0.8s ease, color 0.15s", "&:hover": { transform: "rotate(360deg)", color: "primary.main" } }}
                          >
                            <VolumeUpOutlinedIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  );
                })}
                {!String(wp.invitation || "").trim() && (
                  <Alert severity="warning" sx={{ fontSize: "0.8125rem" }}>{t("invitationRequired")}</Alert>
                )}
              </Box>
            )}
            {profile.languages.length >= 2 && profile.locales?.languageOffer?.enabled !== false && (
              <TextField
                fullWidth size="small" sx={{ mt: 2 }}
                label={`${t("languageOfferLabel")} (${label})`}
                value={profile.locales?.[code]?.languageOfferText || ""}
                onChange={(e) => dispatch(setLocaleOfferText({ lang: code, value: e.target.value }))}
                helperText={t("languageOfferHelp")}
                inputProps={{ dir }}
              />
            )}
          </Box>
        );
      })}

      {profile.languages.length >= 2 && (
        <FormControlLabel
          sx={{ ml: 0, mb: 1, alignItems: "flex-start" }}
          control={
            <Switch
              size="small"
              checked={profile.locales?.languageOffer?.enabled !== false}
              onChange={(e) => dispatch(setLanguageOfferEnabled(e.target.checked))}
            />
          }
          label={
            <Box>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>{t("languageOfferToggle")}</Typography>
              <Typography variant="caption" sx={{ color: "text.disabled" }}>{t("languageOfferToggleHelp")}</Typography>
            </Box>
          }
        />
      )}

      {/* Save row */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 1.5, mt: 3 }}>
        {saved && <Alert severity="success" sx={{ py: 0.25, px: 1.5, borderRadius: 2, fontSize: "0.8125rem" }}>{t("saved")}</Alert>}
        {err   && <Alert severity="error"   sx={{ py: 0.25, px: 1.5, borderRadius: 2, fontSize: "0.8125rem" }} onClose={() => setErr("")}>{err}</Alert>}
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : null}
          sx={{ fontWeight: 700, background: "var(--grad-brand-135)", borderRadius: 2, "&:hover": { filter: "brightness(1.06)", background: "var(--grad-brand-135)" } }}
        >
          {saving ? t("saving") : t("save")}
        </Button>
      </Box>
    </Box>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// Arabic dialect + voice selector — only shown when Arabic is an enabled language
// ══════════════════════════════════════════════════════════════════════════════

const AR_DIALECT_GROUPS = [
  { group: "Levantine", items: [
    { code: "ar-JO", label: "Jordanian — الأردني" },
    { code: "ar-SY", label: "Syrian — الشامي" },
    { code: "ar-LB", label: "Lebanese — اللبناني" },
    { code: "ar-PS", label: "Palestinian — الفلسطيني" },
  ]},
  { group: "Iraqi", items: [
    { code: "ar-IQ", label: "Iraqi — العراقي" },
  ]},
  { group: "Gulf", items: [
    { code: "ar-SA", label: "Saudi — السعودي" },
    { code: "ar-AE", label: "Emirati — الإماراتي" },
    { code: "ar-KW", label: "Kuwaiti — الكويتي" },
    { code: "ar-QA", label: "Qatari — القطري" },
    { code: "ar-BH", label: "Bahraini — البحريني" },
    { code: "ar-OM", label: "Omani — العُماني" },
  ]},
  { group: "Egyptian", items: [
    { code: "ar-EG", label: "Egyptian — المصري" },
  ]},
  { group: "Maghrebi", items: [
    { code: "ar-MA", label: "Moroccan — المغربي" },
    { code: "ar-DZ", label: "Algerian — الجزائري" },
    { code: "ar-TN", label: "Tunisian — التونسي" },
    { code: "ar-LY", label: "Libyan — الليبي" },
  ]},
  { group: "Other", items: [
    { code: "ar-SD", label: "Sudanese — السوداني" },
    { code: "ar-YE", label: "Yemeni — اليمني" },
  ]},
  { group: "Fusha / MSA", items: [
    { code: "ar", label: "Modern Standard Arabic — فصحى" },
  ]},
];

const DIALECT_TO_GROUP = {
  "ar-JO": "levantine", "ar-SY": "levantine", "ar-LB": "levantine", "ar-PS": "levantine",
  "ar-IQ": "iraqi",
  "ar-SA": "gulf", "ar-AE": "gulf", "ar-KW": "gulf", "ar-QA": "gulf", "ar-BH": "gulf", "ar-OM": "gulf",
  "ar-EG": "egyptian",
  "ar-MA": "maghrebi", "ar-DZ": "maghrebi", "ar-TN": "maghrebi", "ar-LY": "maghrebi",
  "ar-SD": "other", "ar-YE": "other",
  "ar":    "fusha",
};

function resolveVoiceName(voiceMap, dialect, gender) {
  if (!voiceMap || !dialect) return null;
  const g = gender === "female" ? "female" : "male";
  const exact = voiceMap.dialectVoice?.[dialect]?.[g];
  if (exact?.voiceId) return exact.name;
  const grp = DIALECT_TO_GROUP[dialect];
  if (grp) {
    const gv = voiceMap.groupVoice?.[grp]?.[g];
    if (gv?.voiceId) return gv.name;
  }
  return voiceMap.groupVoice?.fusha?.[g]?.name ?? null;
}

// ══════════════════════════════════════════════════════════════════════════════
// IvrDialectDialog — review / edit / approve IVR prompts in the tenant's dialect
// ══════════════════════════════════════════════════════════════════════════════
function IvrDialectDialog({ open, onClose, dialectTag }) {
  const [prompts,    setPrompts]    = useState([]);
  const [loading,    setLoading]    = useState(false);
  const [converting, setConverting] = useState(false);
  const [editMap,    setEditMap]    = useState({});
  const [savingId,   setSavingId]   = useState(null);
  const [actionId,   setActionId]   = useState(null);
  const [error,      setError]      = useState("");

  const ACCENT = "#7C3AED";

  const STATUS_COLORS = {
    pending:   { bg: alpha("#F59E0B", 0.12), color: "#D97706" },
    converted: { bg: alpha("#6366F1", 0.12), color: "#4F46E5" },
    approved:  { bg: alpha("#10B981", 0.12), color: "#059669" },
    rejected:  { bg: alpha("#EF4444", 0.12), color: "#DC2626" },
  };

  const load = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get("/ai/ivr-prompts");
      setPrompts(res.data);
    } catch (e) {
      setError(e.response?.data?.error || "Failed to load prompts.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) { load(); setEditMap({}); setError(""); }
  }, [open]);

  const handleConvert = async (force = false) => {
    setConverting(true); setError("");
    try {
      await axiosInstance.post("/ai/ivr-prompts/convert", { force });
      await load();
    } catch (e) {
      setError(e.response?.data?.error || "Conversion failed.");
    } finally {
      setConverting(false);
    }
  };

  const handleSaveEdit = async (id) => {
    setSavingId(id);
    try {
      const res = await axiosInstance.put(`/ai/ivr-prompts/${id}`, { dialectText: editMap[id] });
      setPrompts(prev => prev.map(p => p.id === id ? { ...p, dialectText: res.data.dialectText, status: res.data.status } : p));
      setEditMap(prev => { const c = { ...prev }; delete c[id]; return c; });
    } catch (e) {
      setError(e.response?.data?.error || "Save failed.");
    } finally {
      setSavingId(null);
    }
  };

  const handleApprove = async (id) => {
    setActionId(`${id}-approve`);
    try {
      await axiosInstance.post(`/ai/ivr-prompts/${id}/approve`);
      setPrompts(prev => prev.map(p => p.id === id ? { ...p, status: "approved" } : p));
    } catch (e) {
      setError(e.response?.data?.error || "Approve failed.");
    } finally {
      setActionId(null);
    }
  };

  const handleReject = async (id) => {
    setActionId(`${id}-reject`);
    try {
      await axiosInstance.post(`/ai/ivr-prompts/${id}/reject`);
      setPrompts(prev => prev.map(p => p.id === id ? { ...p, status: "rejected" } : p));
    } catch (e) {
      setError(e.response?.data?.error || "Revoke failed.");
    } finally {
      setActionId(null);
    }
  };

  const stats = useMemo(() => ({
    total:     prompts.length,
    pending:   prompts.filter(p => p.status === "pending").length,
    converted: prompts.filter(p => p.status === "converted").length,
    approved:  prompts.filter(p => p.status === "approved").length,
    rejected:  prompts.filter(p => p.status === "rejected").length,
  }), [prompts]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth
      PaperProps={{ sx: { borderRadius: "18px", maxHeight: "90vh" } }}
    >
      <DialogTitle sx={{ pb: 1.5, borderBottom: "1px solid", borderColor: "divider" }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2, flexWrap: "wrap" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <RecordVoiceOverOutlinedIcon sx={{ color: ACCENT }} />
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: "1rem", lineHeight: 1.2 }}>
                IVR Dialect Prompts
              </Typography>
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                Review and approve Arabic IVR messages in your configured dialect
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", alignItems: "center" }}>
            {[
              { label: `${stats.total} Total`,          color: "#6B7280" },
              { label: `${stats.pending} Pending`,       color: "#D97706" },
              { label: `${stats.converted} Converted`,   color: "#4F46E5" },
              { label: `${stats.approved} Approved`,     color: "#059669" },
              { label: `${stats.rejected} Rejected`,     color: "#DC2626" },
            ].map(({ label, color }) => (
              <Chip key={label} label={label} size="small"
                sx={{ fontSize: "0.6875rem", fontWeight: 700, borderRadius: "8px",
                  background: alpha(color, 0.1), color }} />
            ))}
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {error && (
          <Alert severity="error" onClose={() => setError("")} sx={{ mx: 2, mt: 2, borderRadius: 2 }}>
            {error}
          </Alert>
        )}

        {!loading && (
          <Box sx={{
            px: 2.5, pt: 2, pb: 1.75,
            display: "flex", gap: 1.5, alignItems: "center",
            borderBottom: "1px solid", borderColor: "divider",
          }}>
            <Button
              variant="contained"
              size="small"
              onClick={() => handleConvert(false)}
              disabled={converting || stats.pending === 0}
              startIcon={converting ? <CircularProgress size={13} color="inherit" /> : null}
              sx={{
                borderRadius: "8px", fontWeight: 700, fontSize: "0.8125rem",
                background: `linear-gradient(135deg, ${ACCENT}, #4F46E5)`,
                "&:hover": { filter: "brightness(1.06)", background: `linear-gradient(135deg, ${ACCENT}, #4F46E5)` },
              }}
            >
              {converting ? "Converting…" : stats.pending === 0 ? "All Converted" : `Convert Pending (${stats.pending})`}
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() => handleConvert(true)}
              disabled={converting}
              sx={{
                borderRadius: "8px", fontWeight: 600, fontSize: "0.8125rem",
                borderColor: alpha(ACCENT, 0.4), color: ACCENT,
                "&:hover": { borderColor: ACCENT, background: alpha(ACCENT, 0.05) },
              }}
            >
              Re-convert All
            </Button>
            {dialectTag && (
              <Chip label={dialectTag} size="small" sx={{ ml: "auto", fontWeight: 700, fontSize: "0.75rem",
                background: alpha(ACCENT, 0.1), color: ACCENT }} />
            )}
          </Box>
        )}

        {/* Stale-dialect warning: converted rows used a different dialect */}
        {!loading && dialectTag && prompts.some(p => p.dialectTag && p.dialectTag !== dialectTag) && (
          <Alert severity="warning" sx={{ mx: 2, mt: 1.5, borderRadius: 2, fontSize: "0.8125rem" }}>
            Some prompts were converted with a different dialect. Use <strong>Re-convert All</strong> to update them to <strong>{dialectTag}</strong>.
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress sx={{ color: ACCENT }} />
          </Box>
        ) : (
          <Box sx={{
            overflowY: "auto", maxHeight: "calc(90vh - 270px)",
            p: 2, display: "flex", flexDirection: "column", gap: 1.5,
          }}>
            {prompts.map((p) => {
              const sc      = STATUS_COLORS[p.status] ?? STATUS_COLORS.pending;
              const curText = p.id in editMap ? editMap[p.id] : (p.dialectText ?? "");
              const isDirty = p.id in editMap && editMap[p.id] !== (p.dialectText ?? "");

              return (
                <Box key={p.id} sx={{
                  borderRadius: "12px", border: "1.5px solid",
                  borderColor: p.status === "approved" ? alpha("#10B981", 0.3) : "divider",
                  overflow: "hidden",
                  flexShrink: 0,
                }}>
                  {/* Row header */}
                  <Box sx={{
                    px: 2, py: 1.25,
                    display: "flex", alignItems: "center", gap: 1.25,
                    background: alpha(sc.color, 0.04),
                    borderBottom: "1px solid", borderColor: "divider",
                    flexWrap: "wrap",
                  }}>
                    <Chip label={p.status} size="small"
                      sx={{ fontWeight: 700, fontSize: "0.6875rem", borderRadius: "6px",
                        background: sc.bg, color: sc.color, minWidth: 72, textTransform: "capitalize" }} />
                    <Typography sx={{ fontWeight: 600, fontSize: "0.875rem", color: "text.primary" }}>
                      {p.displayName}
                    </Typography>
                    <Typography sx={{ fontSize: "0.7rem", color: "text.disabled", fontFamily: "monospace", ml: 0.5 }}>
                      {p.promptKey}
                    </Typography>
                    <Box sx={{ display: "flex", gap: 0.75, ml: "auto" }}>
                      {p.status !== "approved" && (
                        <Button size="small" variant="contained"
                          disabled={!!actionId || (!p.dialectText?.trim() && !editMap[p.id]?.trim())}
                          onClick={() => handleApprove(p.id)}
                          startIcon={
                            actionId === `${p.id}-approve`
                              ? <CircularProgress size={11} color="inherit" />
                              : <CheckCircleIcon sx={{ fontSize: "14px !important" }} />
                          }
                          sx={{
                            borderRadius: "7px", fontWeight: 700, fontSize: "0.75rem",
                            py: 0.25, px: 1.25,
                            background: "linear-gradient(135deg, #10B981, #059669)",
                            "&:hover": { filter: "brightness(1.08)", background: "linear-gradient(135deg, #10B981, #059669)" },
                            "&.Mui-disabled": { opacity: 0.45 },
                          }}
                        >
                          Approve
                        </Button>
                      )}
                      {p.status === "approved" && (
                        <Button size="small" variant="outlined"
                          disabled={!!actionId}
                          onClick={() => handleReject(p.id)}
                          startIcon={actionId === `${p.id}-reject` ? <CircularProgress size={11} color="inherit" /> : null}
                          sx={{
                            borderRadius: "7px", fontWeight: 600, fontSize: "0.75rem",
                            py: 0.25, px: 1.25,
                            borderColor: alpha("#EF4444", 0.4), color: "#DC2626",
                            "&:hover": { borderColor: "#EF4444", background: alpha("#EF4444", 0.05) },
                          }}
                        >
                          Revoke
                        </Button>
                      )}
                    </Box>
                  </Box>

                  {/* Fusha source */}
                  <Box sx={{ px: 2, py: 1.5, borderBottom: "1px dashed", borderColor: "divider" }}>
                    <Typography variant="caption" sx={{
                      display: "block", mb: 0.5, fontWeight: 700, fontSize: "0.625rem",
                      textTransform: "uppercase", letterSpacing: "0.08em", color: "text.disabled",
                    }}>
                      Fusha (source)
                    </Typography>
                    <Typography sx={{
                      direction: "rtl", textAlign: "right",
                      fontSize: "0.9375rem", lineHeight: 1.75, color: "text.secondary",
                    }}>
                      {p.fushaText}
                    </Typography>
                  </Box>

                  {/* Dialect edit */}
                  <Box sx={{ px: 2, py: 1.5 }}>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.75 }}>
                      <Typography variant="caption" sx={{
                        fontWeight: 700, fontSize: "0.625rem",
                        textTransform: "uppercase", letterSpacing: "0.08em",
                        color: p.dialectText ? ACCENT : "text.disabled",
                      }}>
                        {p.dialectTag ? `${p.dialectTag} dialect` : "Dialect (not converted yet)"}
                      </Typography>
                      {isDirty && (
                        <Button size="small" variant="outlined"
                          onClick={() => handleSaveEdit(p.id)}
                          disabled={savingId === p.id}
                          startIcon={savingId === p.id ? <CircularProgress size={11} color="inherit" /> : null}
                          sx={{
                            borderRadius: "6px", fontWeight: 700, fontSize: "0.6875rem",
                            py: 0.125, px: 1, borderColor: alpha(ACCENT, 0.4), color: ACCENT,
                          }}
                        >
                          Save
                        </Button>
                      )}
                    </Box>
                    <TextField
                      multiline fullWidth minRows={1} maxRows={4}
                      value={curText}
                      onChange={(e) => setEditMap(prev => ({ ...prev, [p.id]: e.target.value }))}
                      placeholder="No dialect text yet — click Convert Pending above"
                      inputProps={{ dir: "rtl", style: {
                        textAlign: "right",
                        fontSize: "0.9375rem",
                        lineHeight: 1.75,
                      }}}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "8px",
                          "& fieldset": { borderColor: isDirty ? alpha(ACCENT, 0.5) : "divider" },
                          "&:hover fieldset": { borderColor: alpha(ACCENT, 0.35) },
                          "&.Mui-focused fieldset": { borderColor: ACCENT },
                        },
                      }}
                    />
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2.5, py: 1.75, borderTop: "1px solid", borderColor: "divider" }}>
        <Typography variant="caption" sx={{ color: "text.disabled", mr: "auto", fontSize: "0.75rem" }}>
          Approved prompts are used for IVR dialect TTS playback
        </Typography>
        <Button onClick={onClose} sx={{ borderRadius: "8px", fontWeight: 600 }}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

function ArabicDialectCard({ glassSx }) {
  const { t } = useTranslation("profile");

  const [dialect,   setDialect]   = useState(null);
  const [gender,    setGender]    = useState("male");
  const [voiceMap,  setVoiceMap]  = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [saving,    setSaving]    = useState(false);
  const [saved,     setSaved]     = useState(false);
  const [err,       setErr]       = useState("");
  const [ivrOpen,   setIvrOpen]   = useState(false);
  const [ackBank,   setAckBank]   = useState(true);      // tenant switch for the acknowledgement bank
  const [ackPlatform, setAckPlatform] = useState(false); // platform switch (admin); when off the tenant switch is moot
  // What the card needs to be right, beyond the tag: the tenant's own description of their variety
  // ("Amman, urban, formal clinic") and the currency their prices are spoken in.
  const [varietyNotes, setVarietyNotes] = useState("");
  const [currency,     setCurrency]     = useState("");
  // The spoken-language reference the agent actually speaks with. Editable, regenerable, measured.
  const [card,      setCard]      = useState(null);   // { dialect, card, status, source, version, reviewed, editableFields }
  const [cardEdits, setCardEdits] = useState(null);   // the tenant's in-progress edits, keyed by field
  
  const [cardBusy,  setCardBusy]  = useState("");     // "" | "saving" | "regenerating" | "restoring"
  const [cardMsg,   setCardMsg]   = useState("");

  const loadCard = () => axiosInstance.get("/ai/dialect-card")
    .then((res) => { setCard(res.data); setCardEdits(null); })
    .catch(() => setCard(null));

  useEffect(() => {
    axiosInstance.get("/ai/voice-config")
      .then((res) => {
        setDialect(res.data.dialect || null);
        setGender(res.data.gender  || "male");
        setVoiceMap(res.data.voiceMap || null);
        setAckBank(res.data.ackBankEnabled !== false);
        setAckPlatform(!!res.data.ackBankPlatform);
        setVarietyNotes(res.data.varietyNotes || "");
        setCurrency(res.data.currency || "");
      })
      .catch(() => {})
      .finally(() => setLoading(false));
    loadCard();
  }, []);

  const previewVoiceName = useMemo(
    () => resolveVoiceName(voiceMap, dialect, gender),
    [voiceMap, dialect, gender]
  );

  const handleSave = async () => {
    setSaving(true); setSaved(false); setErr("");
    try {
      const res = await axiosInstance.put("/ai/voice-config", { dialect, gender, ackBankEnabled: ackBank, varietyNotes, currency: currency || undefined });
      setDialect(res.data.dialect || dialect);
      setGender(res.data.gender  || gender);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
      // A save that changed the dialect either applied the platform's approved card for that
      // variety — instant — or started one being written, which takes minutes. In the second
      // case the page used to go BLANK: `card` still held the previous dialect, the panel is
      // gated on card.dialect === dialect, and the Building chip, the message line and the
      // error are all inside that gate. Seed a placeholder for the new dialect so there is
      // something to render, carrying the real editableFields because saveCard iterates them.
      if (res.data.card === "building") {
        setCard((prev) => ({
          dialect: res.data.dialect || dialect, name: null, card: null, status: "building",
          source: null, generatedAt: null, previous: null,
          editableFields: prev?.editableFields ?? [],
        }));
        setCardEdits(null);
        setCardBusy("regenerating"); setCardMsg("");
        // Let go of the Save button: the build is followed by its own indicator, and holding
        // `saving` across a ten-minute poll made the whole form look wedged.
        setSaving(false);
        try { await waitForCard(); } finally { setCardBusy(""); }
      } else loadCard();
    } catch (e) {
      setErr(e.response?.data?.error || t("failedSave"));
    } finally {
      setSaving(false);
    }
  };

  const ACCENT = "#7C3AED";

  const fieldValue = (k) => {
    if (cardEdits && k in cardEdits) return cardEdits[k];
    const v = card?.card?.[k];
    if (Array.isArray(v)) return v.join(" | ");
    if (v && typeof v === "object") return Object.entries(v).map(([lvl, txt]) => `${lvl}: ${txt}`).join("\n");
    return v ?? "";
  };
  const setField = (k, v) => setCardEdits({ ...(cardEdits || {}), [k]: v });

  // Import a reviewed card. It fills the fields and stops there — nothing is saved until the
  // operator has read what landed, because a paste is exactly where a wrong card gets stored
  // without anyone seeing it. Values are converted into the same shapes the boxes already use,
  // so the save path below is untouched and what is stored is what was reviewed.
  const toFieldText = (v) => {
    if (Array.isArray(v)) return v.map((x) => String(x).trim()).filter(Boolean).join(" | ");
    if (v && typeof v === "object") return Object.entries(v).map(([lvl, txt]) => `${lvl}: ${txt}`).join("\n");
    return v == null ? "" : String(v);
  };


  const saveCard = async () => {
    setCardBusy("saving"); setCardMsg("");
    try {
      const payload = {};
      for (const k of card.editableFields) {
        const raw = fieldValue(k);
        if (k === "registerNotes") {
          const o = {};
          String(raw).split("\n").forEach((line) => { const m = line.match(/^\s*(formal|warm|casual)\s*:\s*(.+)$/i); if (m) o[m[1].toLowerCase()] = m[2].trim(); });
          if (Object.keys(o).length) payload[k] = o;
        } else payload[k] = raw;
      }
      const res = await axiosInstance.put("/ai/dialect-card", { card: payload });
      setCard({ ...card, ...res.data });
      setCardEdits(null);
      setCardMsg(t("cardSaved", { defaultValue: "Reference saved. Your wording now outranks the generated one." }));
    } catch (e) {
      setCardMsg((e.response?.data?.errors || [e.response?.data?.error || t("failedSave")]).join("; "));
    } finally { setCardBusy(""); }
  };
  // Building the card is a background job on the server — started by a save that changes the
  // dialect, or by the regenerate button — and the card lands when the model is done, which on a
  // large model is longer than a proxy will wait on one HTTP request. So the page polls: up to
  // ten minutes, every three seconds; the server says building / ready / failed.
  const waitForCard = async () => {
    const started = Date.now();
    for (;;) {
      await new Promise((r) => setTimeout(r, 3000));
      const res = await axiosInstance.get("/ai/dialect-card");
      if (res.data.status === "failed") { setCard(res.data); setCardMsg(res.data.error || t("failedSave")); return "failed"; }
      if (res.data.status === "ready") { setCard(res.data); setCardEdits(null); setCardMsg(t("cardRegenerated", { defaultValue: "Reference regenerated from your dialect, gender, currency and notes." })); return "ready"; }
      if (Date.now() - started > 10 * 60 * 1000) { setCardMsg(t("cardStillBuilding", { defaultValue: "Still building — it will appear here when the model is done." })); return "timeout"; }
    }
  };
  // Regenerate REPLACES the reference, including one the tenant wrote by hand — the server's
  // manual-card guard is deliberately overridden here, because pressing this button is the
  // tenant asking. It was wired straight to the POST with nothing in between, and a card that
  // had been corrected over several sittings went in one click.
  const [confirmRegen, setConfirmRegen] = useState(false);
  const regenerateCard = async () => {
    setConfirmRegen(false);
    setCardBusy("regenerating"); setCardMsg("");
    try { await axiosInstance.post("/ai/dialect-card/regenerate"); await waitForCard(); }
    catch (e) { setCardMsg(e.response?.data?.error || e.response?.data?.detail || e.message || t("failedSave")); }
    finally { setCardBusy(""); }
  };

  // One step back. The server keeps the card each write replaced, and restoring is itself a
  // write — so this is undoable too, and pressing it twice returns to where you started.
  const restoreCard = async () => {
    setCardBusy("restoring"); setCardMsg("");
    try {
      await axiosInstance.post("/ai/dialect-card/restore");
      const res = await axiosInstance.get("/ai/dialect-card");
      setCard(res.data); setCardEdits(null);
      setCardMsg(t("cardRestored", { defaultValue: "The previous reference is back. The sound check is running against it now." }));
    } catch (e) { setCardMsg(e.response?.data?.error || e.message || t("failedSave")); }
    finally { setCardBusy(""); }
  };


  // Field groups, in the order a reader wants them. Labels are English UI copy; the values are
  // the tenant's Arabic, in their own script.
  const CARD_GROUPS = [
    { title: "How it sounds",           keys: ["spelling", "vowelMarks", "alwaysMark", "avoidSpelling"] },
    { title: "Numbers, time and money", keys: ["digits", "numbers", "timeAndDate", "money"] },
    { title: "Words and grammar",       keys: ["questionWords", "negation", "tenseMarkers", "addressForms", "selfForms", "avoid"] },
    { title: "What the agent says",     keys: ["everydayPhrases", "fillers", "acknowledgements", "holdingPhrases", "farewells", "examples"] },
    { title: "Register",                keys: ["registerNotes"] },
  ];
  

  return (
    <Box sx={{ mt: 2.5, p: 3, borderRadius: "18px", ...glassSx }}>
      {/* header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
        <Box sx={{
          width: 38, height: 38, borderRadius: "11px",
          background: alpha(ACCENT, 0.1),
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <RecordVoiceOverOutlinedIcon sx={{ fontSize: 20, color: ACCENT }} />
        </Box>
        <Box>
          <Typography sx={{ fontWeight: 600, fontSize: "0.9375rem", color: "text.primary", lineHeight: 1.2 }}>
            {t("arabicDialectVoice", { defaultValue: "Arabic Dialect & Voice" })}
          </Typography>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            {t("arabicDialectSubtitle", { defaultValue: "AI agent responds in this dialect for Arabic calls" })}
          </Typography>
        </Box>
        {dialect && (
          <Chip
            label={dialect}
            size="small"
            sx={{
              ml: "auto", fontWeight: 700, fontSize: "0.75rem",
              background: alpha(ACCENT, 0.1), color: ACCENT,
            }}
          />
        )}
      </Box>

      <Divider sx={{ mb: 2.5 }} />

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
          <CircularProgress size={22} sx={{ color: ACCENT }} />
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
          {/* Dialect selector */}
          <FormControl size="medium" fullWidth>
            <InputLabel>{t("arabicDialect", { defaultValue: "Arabic Dialect" })}</InputLabel>
            <Select
              value={dialect || ""}
              label={t("arabicDialect", { defaultValue: "Arabic Dialect" })}
              onChange={(e) => setDialect(e.target.value || null)}
              sx={{ borderRadius: "10px" }}
            >
              <MenuItem value="">
                <Typography sx={{ color: "text.disabled", fontSize: "0.875rem" }}>
                  {t("notConfigured", { defaultValue: "Not configured" })}
                </Typography>
              </MenuItem>
              {AR_DIALECT_GROUPS.map(({ group, items }) => [
                <ListSubheader key={group} sx={{
                  fontSize: "0.6875rem", fontWeight: 700, textTransform: "uppercase",
                  letterSpacing: "0.08em", color: "text.disabled",
                  lineHeight: "2rem", background: "var(--surface-card)",
                }}>
                  {group}
                </ListSubheader>,
                ...items.map(({ code, label }) => (
                  <MenuItem key={code} value={code} sx={{ pl: 3 }}>
                    <Typography sx={{ fontSize: "0.875rem", dir: "ltr" }}>{label}</Typography>
                  </MenuItem>
                )),
              ])}
            </Select>
          </FormControl>

          {/* Gender toggle */}
          <Box>
            <Typography variant="caption" sx={{
              display: "block", mb: 1, color: "text.disabled",
              textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 700, fontSize: "0.6875rem",
            }}>
              {t("voiceGender", { defaultValue: "Voice Gender" })}
            </Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              {["male", "female"].map((g) => (
                <Box
                  key={g}
                  onClick={() => setGender(g)}
                  sx={{
                    px: 2.5, py: 1, borderRadius: "10px", cursor: "pointer",
                    border: "1.5px solid",
                    borderColor: gender === g ? ACCENT : "divider",
                    background: gender === g ? alpha(ACCENT, 0.07) : "transparent",
                    transition: "all 0.12s",
                    "&:hover": gender !== g ? { borderColor: alpha(ACCENT, 0.35) } : {},
                  }}
                >
                  <Typography sx={{
                    fontSize: "0.875rem", fontWeight: gender === g ? 700 : 500,
                    color: gender === g ? ACCENT : "text.secondary",
                  }}>
                    {g === "male"
                      ? t("male",   { defaultValue: "Male" })
                      : t("female", { defaultValue: "Female" })}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>

          {/* Resolved voice preview */}
          {dialect && (
            <Box sx={{
              p: 1.5, px: 2, borderRadius: "10px",
              background: alpha(ACCENT, 0.05),
              border: `1px solid ${alpha(ACCENT, 0.15)}`,
              display: "flex", alignItems: "center", gap: 1,
            }}>
              <RecordVoiceOverOutlinedIcon sx={{ fontSize: 16, color: ACCENT, flexShrink: 0 }} />
              <Box>
                <Typography variant="caption" sx={{ color: "text.disabled", fontSize: "0.6875rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                  {t("resolvedVoice", { defaultValue: "Voice" })}
                </Typography>
                <Typography sx={{ fontSize: "0.8125rem", color: ACCENT, fontWeight: 600, mt: 0.125 }}>
                  {previewVoiceName ?? t("noVoiceAvailable", { defaultValue: "No voice available — will use default" })}
                </Typography>
              </Box>
            </Box>
          )}

          {/* What the reference is built from, beyond the tag */}
          {dialect && (
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 140px" }, gap: 1.5 }}>
              <TextField
                size="small"
                label={t("currency", { defaultValue: "Currency" })}
                placeholder="JOD"
                value={currency}
                onChange={(e) => setCurrency(e.target.value.toUpperCase().replace(/[^A-Z]/g, "").slice(0, 3))}
                disabled={!!card?.currencyFromTemplate}
                helperText={card?.currencyFromTemplate
                  ? t("currencyLocked", { defaultValue: "Set by the approved reference for this variety, which already speaks this currency in words." })
                  : t("currencyHelp", { defaultValue: "How prices are spoken." })}
                inputProps={{ style: { textTransform: "uppercase", letterSpacing: "0.1em" } }}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
              />
            </Box>
          )}

          {/* Save row */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mt: 0.5, flexWrap: "wrap" }}>
            {dialect && dialect !== "ar" && (
              <Button
                variant="outlined"
                size="small"
                onClick={() => setIvrOpen(true)}
                startIcon={<RecordVoiceOverOutlinedIcon sx={{ fontSize: "16px !important" }} />}
                sx={{
                  borderRadius: 2, fontWeight: 600, fontSize: "0.8125rem",
                  borderColor: alpha(ACCENT, 0.4), color: ACCENT,
                  "&:hover": { borderColor: ACCENT, background: alpha(ACCENT, 0.05) },
                }}
              >
                {t("reviewIvrPrompts", { defaultValue: "Review IVR Prompts" })}
              </Button>
            )}
            <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 1.5 }}>
              {saved && (
                <Alert severity="success" sx={{ py: 0.25, px: 1.5, borderRadius: 2, fontSize: "0.8125rem" }}>
                  {t("saved", { defaultValue: "Saved" })}
                </Alert>
              )}
              {err && (
                <Alert severity="error" sx={{ py: 0.25, px: 1.5, borderRadius: 2, fontSize: "0.8125rem" }} onClose={() => setErr("")}>
                  {err}
                </Alert>
              )}
              <FormControlLabel sx={{ mb: 1.5, display: "flex", alignItems: "flex-start", ml: 0 }}
                control={<Switch size="small" checked={ackBank && ackPlatform} disabled={!ackPlatform} onChange={(e) => setAckBank(e.target.checked)} />}
                label={<Box><Typography sx={{ fontSize: "0.85rem", fontWeight: 600 }}>{t("ackBankToggle", { defaultValue: "Instant acknowledgements" })}</Typography>
                  <Typography variant="caption" sx={{ color: "text.secondary" }}>{ackPlatform ? t("ackBankHelp", { defaultValue: "Short pre-recorded fillers the agent can play while it thinks. Off = never generated, never played." }) : t("ackBankOff", { defaultValue: "Disabled platform-wide by the administrator." })}</Typography></Box>} />
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={saving || !dialect}
                startIcon={saving ? <CircularProgress size={14} color="inherit" /> : null}
                sx={{
                  fontWeight: 700, borderRadius: 2,
                  background: `linear-gradient(135deg, ${ACCENT} 0%, #4F46E5 100%)`,
                  "&:hover": { filter: "brightness(1.06)", background: `linear-gradient(135deg, ${ACCENT} 0%, #4F46E5 100%)` },
                  "&.Mui-disabled": { opacity: 0.5 },
                }}
              >
                {saving ? t("saving", { defaultValue: "Saving…" }) : t("save", { defaultValue: "Save" })}
              </Button>
            </Box>
          </Box>
        </Box>
      )}


      {/* The spoken-language reference the agent speaks with. */}
      {/* `|| !!cardBusy` keeps the panel up while a reference is being written: without it the
          dialect changes, the stored card still belongs to the old one, and every indicator
          that something is happening is hidden by this very gate. */}
      {!loading && dialect && card && (card.dialect === dialect || !!cardBusy) && (
        <Box sx={{ mt: 3, pt: 2.5, borderTop: "1px solid", borderColor: "divider" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mb: 1.5 }}>
            <Typography sx={{ fontWeight: 600, fontSize: "0.9375rem" }}>
              {t("dialectReference", { defaultValue: "Spoken-language reference" })}
            </Typography>
            <Chip size="small" label={card.status !== "ready" ? t("refBuilding", { defaultValue: "Building…" })
              : card.source === "manual" ? t("refManual", { defaultValue: "Your wording" })
              : card.source === "template" ? t("refTemplate", { defaultValue: "Platform default" })
              : t("refGenerated", { defaultValue: "Generated" })}
              sx={{ fontWeight: 700, fontSize: "0.6875rem", background: alpha(ACCENT, 0.1), color: ACCENT }} />
            {card.version && <Chip size="small" variant="outlined" label={`v${card.version}${card.reviewed === false ? " · unreviewed" : ""}`} sx={{ fontSize: "0.6875rem" }} />}
            <Box sx={{ ml: "auto", display: "flex", gap: 1 }}>
              {card.previous && (
                <Button size="small" variant="outlined" disabled={!!cardBusy} onClick={restoreCard}
                  sx={{ borderRadius: 2, fontSize: "0.75rem", fontWeight: 600, borderColor: alpha(ACCENT, 0.4), color: ACCENT }}>
                  {cardBusy === "restoring" ? t("restoring", { defaultValue: "Restoring…" }) : t("restorePrevious", { defaultValue: "Undo — restore the previous" })}
                </Button>
              )}
              <Button size="small" variant="outlined" disabled={!!cardBusy} onClick={() => setConfirmRegen(true)}
                sx={{ borderRadius: 2, fontSize: "0.75rem", fontWeight: 600, borderColor: alpha(ACCENT, 0.4), color: ACCENT }}>
                {cardBusy === "regenerating" ? t("regenerating", { defaultValue: "Regenerating…" }) : t("regenerate", { defaultValue: "Regenerate" })}
              </Button>
            </Box>
          </Box>


          {/* The card, editable. */}
          {!card.card && !!cardBusy && (
            <Typography sx={{ fontSize: "0.8125rem", color: "text.secondary", mb: 1.5 }}>
              {t("refBuildingHelp", { defaultValue: "No approved reference exists for this variety yet, so one is being written for you. It takes a few minutes and the page updates by itself. You can leave this page; it carries on without you." })}
            </Typography>
          )}
          {card.card && CARD_GROUPS.map(({ title, keys }) => (
            <Box key={title} sx={{ mb: 2 }}>
              <Typography variant="caption" sx={{ display: "block", mb: 1, color: "text.disabled", textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 700, fontSize: "0.6875rem" }}>{title}</Typography>
              <Box sx={{ display: "grid", gap: 1.25 }}>
                {keys.filter(k => card.editableFields.includes(k)).map((k) => (
                  <TextField key={k} size="small" multiline minRows={1} maxRows={4} label={k}
                    value={fieldValue(k)} onChange={(e) => setField(k, e.target.value)}
                    inputProps={{ dir: "auto", style: { fontSize: "0.875rem", lineHeight: 1.6 } }}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }} />
                ))}
              </Box>
            </Box>
          ))}

          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
            {cardMsg && <Alert severity={/saved|regenerated|filled/i.test(cardMsg) ? "success" : "warning"} sx={{ py: 0.25, px: 1.5, borderRadius: 2, fontSize: "0.8125rem" }} onClose={() => setCardMsg("")}>{cardMsg}</Alert>}
            <Button variant="contained" size="small" disabled={!cardEdits || !!cardBusy} onClick={saveCard}
              sx={{ fontWeight: 700, borderRadius: 2, background: `linear-gradient(135deg, ${ACCENT} 0%, #4F46E5 100%)` }}>
              {cardBusy === "saving" ? t("saving", { defaultValue: "Saving…" }) : t("saveReference", { defaultValue: "Save my wording" })}
            </Button>
          </Box>


          <Dialog open={confirmRegen} onClose={() => setConfirmRegen(false)} fullWidth maxWidth="sm">
            <DialogTitle sx={{ fontWeight: 700 }}>{t("regenConfirmTitle", { defaultValue: "Replace the spoken-language reference?" })}</DialogTitle>
            <DialogContent>
              <Typography sx={{ fontSize: "0.875rem", color: "text.secondary" }}>
                {card?.source === "manual"
                  ? t("regenConfirmManual", { defaultValue: "This reference is your own wording. Regenerating writes a new one from your dialect, gender, currency and notes, and every correction you have made here is replaced. A save never does this — only this button does." })
                  : t("regenConfirmBody", { defaultValue: "A new reference is written from your dialect, gender, currency and notes. The one on screen now is replaced." })}
              </Typography>
              <Typography sx={{ fontSize: "0.875rem", color: "text.secondary", mt: 1.5 }}>
                {t("regenConfirmUndo", { defaultValue: "The reference being replaced is kept, so you can undo this afterwards. It takes a few minutes and the sound check runs on the new one automatically." })}
              </Typography>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button onClick={() => setConfirmRegen(false)}>{t("cancel", { defaultValue: "Cancel" })}</Button>
              <Button variant="contained" color="warning" onClick={regenerateCard} sx={{ fontWeight: 700, borderRadius: 2 }}>
                {t("regenConfirmGo", { defaultValue: "Replace it" })}
              </Button>
            </DialogActions>
          </Dialog>


          <Typography variant="caption" sx={{ display: "block", mt: 1, color: "text.secondary" }}>
            {t("refHelp", { defaultValue: "Arrays are separated with |. Register lines start with formal:, warm: or casual:. This reference starts from the platform's default for your variety; the moment you change a line it is yours, it stays with your account only, and it survives every save. Regenerate replaces it — it asks first, and the reference it replaced is kept so you can undo it." })}
          </Typography>
        </Box>
      )}

      <IvrDialectDialog
        open={ivrOpen}
        onClose={() => setIvrOpen(false)}
        dialectTag={dialect}
      />
    </Box>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// NonArVoiceCard — voice gender selection for non-Arabic enabled languages
// ══════════════════════════════════════════════════════════════════════════════
const LANG_LABELS = { en: "English", fr: "French", es: "Spanish", pt: "Portuguese" };

function NonArVoiceCard({ languages, glassSx }) {
  const ACCENT = "#7C3AED";
  const [genders,  setGenders]  = useState({});
  const [saving,   setSaving]   = useState({});
  const [saved,    setSaved]    = useState({});
  const [err,      setErr]      = useState({});
  const [loading,  setLoading]  = useState(true);

  const nonArLangs = (languages || []).filter(l => l !== "ar");

  useEffect(() => {
    if (!nonArLangs.length) { setLoading(false); return; }
    axiosInstance.get("/ai/voice-config")
      .then(res => {
        const ng = res.data.nonArGenders || {};
        // Default to "male" for any language not yet configured
        const defaults = {};
        nonArLangs.forEach(l => { defaults[l] = ng[l] || "male"; });
        setGenders(defaults);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [languages?.join(",")]);

  const handleSave = async (lang) => {
    setSaving(prev => ({ ...prev, [lang]: true }));
    setErr(prev => ({ ...prev, [lang]: "" }));
    try {
      await axiosInstance.put("/ai/voice-config", { lang, gender: genders[lang] || "male" });
      setSaved(prev => ({ ...prev, [lang]: true }));
      setTimeout(() => setSaved(prev => ({ ...prev, [lang]: false })), 3000);
    } catch (e) {
      setErr(prev => ({ ...prev, [lang]: e.response?.data?.error || "Save failed." }));
    } finally {
      setSaving(prev => ({ ...prev, [lang]: false }));
    }
  };

  if (!nonArLangs.length) return null;

  return (
    <Box sx={{ mt: 2.5, p: 3, borderRadius: "18px", ...glassSx }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
        <Box sx={{
          width: 38, height: 38, borderRadius: "11px",
          background: alpha(ACCENT, 0.1),
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <RecordVoiceOverOutlinedIcon sx={{ fontSize: 20, color: ACCENT }} />
        </Box>
        <Box>
          <Typography sx={{ fontWeight: 600, fontSize: "0.9375rem", color: "text.primary", lineHeight: 1.2 }}>
            Language Voice
          </Typography>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            Choose a voice gender for each enabled language
          </Typography>
        </Box>
      </Box>

      <Divider sx={{ mb: 2.5 }} />

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
          <CircularProgress size={22} sx={{ color: ACCENT }} />
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
          {nonArLangs.map(lang => (
            <Box key={lang}>
              <Typography sx={{ fontWeight: 600, fontSize: "0.875rem", mb: 1.5, color: "text.primary" }}>
                {LANG_LABELS[lang] ?? lang.toUpperCase()}
              </Typography>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
                {["male", "female"].map(g => {
                  const active = (genders[lang] || "male") === g;
                  return (
                    <Chip
                      key={g}
                      label={g === "male" ? "Male" : "Female"}
                      onClick={() => setGenders(prev => ({ ...prev, [lang]: g }))}
                      variant={active ? "filled" : "outlined"}
                      sx={{
                        fontWeight: 700,
                        fontSize: "0.8125rem",
                        borderRadius: "10px",
                        cursor: "pointer",
                        borderColor: active ? ACCENT : "divider",
                        background: active ? alpha(ACCENT, 0.12) : "transparent",
                        color: active ? ACCENT : "text.secondary",
                        "& .MuiChip-label": { px: 1.5 },
                        "&:hover": { background: alpha(ACCENT, 0.08), borderColor: ACCENT },
                      }}
                    />
                  );
                })}
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => handleSave(lang)}
                  disabled={saving[lang]}
                  startIcon={saving[lang] ? <CircularProgress size={13} color="inherit" /> : null}
                  sx={{
                    ml: "auto", minWidth: 80, borderRadius: "10px", fontWeight: 700,
                    background: saved[lang]
                      ? "linear-gradient(135deg,#10B981,#059669)"
                      : `linear-gradient(135deg,${ACCENT},#4F46E5)`,
                    "&:hover": { filter: "brightness(1.06)" },
                  }}
                >
                  {saving[lang] ? "…" : saved[lang] ? "Saved!" : "Save"}
                </Button>
              </Box>
              {err[lang] && (
                <Typography variant="caption" sx={{ color: "error.main", mt: 0.5, display: "block" }}>
                  {err[lang]}
                </Typography>
              )}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// WorkingHoursCard - the days and times the business is open, and the timezone they are read in.
//
// These lived behind a button inside the call flow cards editor, which is not where anyone looks
// for them: they are a fact about the business, not a property of one call flow. The editor itself
// is unchanged, only its home.
//
// Closing it SAVES. It did not before: the dialog wrote to the store and the store reached the
// server only if the tenant happened to change a language afterwards, so hours could be set,
// confirmed, and quietly lost.
function WorkingHoursCard({ glassSx }) {
  const { t, i18n } = useTranslation("profile");
  const lang = i18n.language;
  const dispatch = useDispatch();
  const profile = useSelector((s) => s.profile.profile);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const days = profile?.workingDays || {};
  // The stored keys ('Tus', 'Thur') are data: shown as the day's name, in the week's order (they were shown raw, as saved).
  const openDays = sortDayEntries(Object.entries(days).filter(([, v]) => v?.from && v?.to), lang);
  // A business on UTC that is not in UTC: its assistant's hours and the times it says are off. Every account was set up on
  // UTC until 4.3d.4, so the one already set is checked against where the person looking is.
  const zone = profile?.timezone || "UTC";
  const here = browserZone();
  const zoneSuspect = zone === "UTC" && here !== "UTC";
  const useHere = async () => {
    dispatch(setTimezone({ value: here }));
    setSaving(true);
    try {
      await dispatch(saveProfileMeta()).unwrap();
      dispatch(showToast({ message: t("hoursSaved"), severity: "success" }));
    } catch {
      dispatch(showToast({ message: t("hoursSaveFailed"), severity: "error" }));
    } finally { setSaving(false); }
  };

  const close = async () => {
    setOpen(false);
    setSaving(true);
    try {
      await dispatch(saveProfileMeta()).unwrap();
      dispatch(showToast({ message: t("hoursSaved"), severity: "success" }));
    } catch {
      dispatch(showToast({ message: t("hoursSaveFailed"), severity: "error" }));
    } finally { setSaving(false); }
  };

  return (
    <Box sx={{ ...glassSx, borderRadius: 3, p: { xs: 2.5, md: 3 }, mb: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
        <Box sx={{ width: 38, height: 38, borderRadius: "11px", background: alpha("#4F46E5", 0.1), display: "flex", alignItems: "center", justifyContent: "center" }}>
          <AccessTimeOutlinedIcon sx={{ fontSize: 20, color: "#4F46E5" }} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontWeight: 600, fontSize: "0.9375rem", color: "text.primary", lineHeight: 1.2 }}>
            {t("workingHoursCard")}
          </Typography>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            {t("workingHoursCardDesc")}
          </Typography>
        </Box>
        <Button
          variant="outlined" size="small" disabled={saving}
          startIcon={saving ? <CircularProgress size={13} /> : <AccessTimeOutlinedIcon sx={{ fontSize: 16 }} />}
          onClick={() => setOpen(true)}
          sx={{ ml: "auto", flexShrink: 0, textTransform: "none", fontWeight: 600, borderColor: "var(--line-200)", color: "text.primary" }}
        >
          {t("editHours")}
        </Button>
      </Box>

      <Divider sx={{ mb: 2 }} />

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
        {openDays.length === 0 ? (
          <Typography variant="body2" sx={{ color: "text.secondary" }}>{t("noHoursYet")}</Typography>
        ) : openDays.map(([day, v]) => (
          <Box
            key={day}
            sx={{
              display: "inline-flex", alignItems: "center", gap: "var(--sp-1)",
              px: "var(--sp-3)", py: "var(--sp-1)", borderRadius: "var(--r-pill)",
              background: "var(--c-violet-bg)", color: "var(--ink-800)",
              fontFamily: "var(--font-ui)", fontSize: "var(--t-caption)", fontWeight: 600, fontVariantNumeric: "tabular-nums",
            }}
          >
            <span>{dayName(day, lang, "short")}</span>
            <bdi dir="ltr">{v.from}–{v.to}</bdi>
            {isOvernight(v.from, v.to) && <Moon size={12} aria-label={t("hoursOvernight")} />}
          </Box>
        ))}
      </Box>

      {/* The business's time zone: every time in the app is read and shown in it. */}
      <Box sx={{ display: "flex", alignItems: "center", gap: "var(--sp-2)", mt: "var(--sp-4)", color: "var(--ink-600)" }}>
        <Globe size={14} aria-hidden="true" />
        <Typography sx={{ fontFamily: "var(--font-ui)", fontSize: "var(--t-caption)", color: "var(--ink-600)" }}>
          {t("timezoneIs", { tz: tzLabel(zone) })}
        </Typography>
      </Box>
      {zoneSuspect && (
        <Box role="alert" sx={{ mt: "var(--sp-3)", p: "var(--sp-3)", borderRadius: "var(--r-md)", background: "var(--warning-bg)", display: "flex", alignItems: "center", gap: "var(--sp-3)", flexWrap: "wrap" }}>
          <Box sx={{ color: "var(--warning)", display: "inline-flex" }}><AlertTriangle size={16} aria-hidden="true" /></Box>
          <Typography sx={{ flex: 1, minWidth: 220, fontFamily: "var(--font-ui)", fontSize: "var(--t-body-sm)", color: "var(--ink-800)" }}>
            {t("timezoneUtcWarning")}
          </Typography>
          <TlButton variant="secondary" size="sm" loading={saving} onClick={useHere}>{t("timezoneUseHere", { tz: tzLabel(here) })}</TlButton>
        </Box>
      )}

      <Dialog open={open} onClose={close} maxWidth="md" fullWidth>
        <DaysOfWork onClose={close} />
      </Dialog>
    </Box>
  );
}

const Profile = () => {
  const { t }       = useTranslation("profile");
  const { mode }    = useColorMode();
  const isLight     = mode === "light";
  const dispatch    = useDispatch();
  const user        = useSelector((s) => s.auth.user);
  const profile     = useSelector((s) => s.profile.profile);
  const userDetails = useSelector((s) => s.auth.userDetails);

  // Above the early return, because a hook may not be called conditionally — below it the hook
  // count changes when accountType flips and React throws. Running it for a subaccount does
  // nothing: /me returns both company names as null for them (server/server.js:1193), and
  // SideBar.jsx:377 already runs this same effect for every account type.
  useEffect(() => {
    if (!userDetails) return;
    if (!profile?.locales?.en?.name?.trim() && userDetails.enCompanyName)
      dispatch(setLocaleName({ lang: "en", value: userDetails.enCompanyName }));
    if (!profile?.locales?.ar?.name?.trim() && userDetails.arCompanyName)
      dispatch(setLocaleName({ lang: "ar", value: userDetails.arCompanyName }));
  }, [userDetails]);

  if (user?.accountType === "subaccount") return <SubAccountProfile />;

  const glassSx = {
    background: "var(--surface-card)",
    border: "1px solid var(--line-100)",
    boxShadow: "var(--shadow-card)",
  };

  const handleTTS = async (text) => {
    if (!text) return;
    try {
      const res = await fetch(
        `${import.meta.env.VITE_TTS_URL || "/heycallcenter/api"}/tts`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: "Bearer " + (localStorage.getItem("token") || "") },
          body: JSON.stringify({
            lang: /[؀-ۿ]/.test(text) ? "ar" : "en",
            items: [{ text, name: "preview" }],
          }),
        }
      );
      const blobs = await res.json();
      const { pcmBase64ToWavDataUrl } = await import("../../../utils/blobHelpers");
      new Audio(pcmBase64ToWavDataUrl(blobs[0].blobBase64)).play();
    } catch { /* silent */ }
  };

  const allLangsFilled = !!(profile?.languages?.length > 0 && profile.languages.every(l => profile?.locales?.[l]?.name?.trim()));
  const bothFilled = allLangsFilled;

  return (
    <Box sx={{ maxWidth: 800, mx: "auto" }}>
      {/* header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, letterSpacing: "-0.02em", color: "text.primary", mb: 0.5 }}>
          {t("companyProfile")}
        </Typography>
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          {t("manageAccountDetails")}
        </Typography>
      </Box>

      {/* unlock banner */}
      {!bothFilled && (
        <Box
          sx={{
            mb: 3, p: 2, px: 2.5, borderRadius: 2,
            background: alpha("#4F46E5", 0.07),
            border: "1px solid", borderColor: alpha("#4F46E5", 0.2),
            display: "flex", alignItems: "center", gap: 1.5,
          }}
        >
          <InfoOutlinedIcon sx={{ fontSize: 18, color: "primary.main", flexShrink: 0 }} />
          <Typography variant="body2" sx={{ color: "primary.main", fontWeight: 500 }}>
            {t("enterBothNamesPrompt")}
          </Typography>
        </Box>
      )}

      {/* account info card */}
      <Box sx={{ mb: 2.5, p: 3, borderRadius: "18px", boxShadow: "var(--shadow-card)", ...glassSx }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
          <Box
            sx={{
              width: 38, height: 38, borderRadius: "11px",
              background: alpha("#4F46E5", 0.1),
              display: "flex", alignItems: "center", justifyContent: "center",
            }}
          >
            <PersonOutlineOutlinedIcon sx={{ fontSize: 20, color: "primary.main" }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 600, fontSize: "0.9375rem", color: "text.primary", lineHeight: 1.2 }}>
              {t("accountInfo")}
            </Typography>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              {t("managedByTeleredo")}
            </Typography>
          </Box>
          <Chip
            label={userDetails?.plan || "Starter"}
            size="small"
            sx={{ ml: "auto", fontWeight: 700, background: alpha("#4F46E5", 0.1), color: "primary.main", fontSize: "0.75rem" }}
          />
        </Box>

        <Divider sx={{ mb: 2 }} />

        <ROW label={t("firstName")} value={userDetails?.firstName} />
        <Divider sx={{ opacity: 0.4 }} />
        <ROW label={t("lastName")}  value={userDetails?.lastName} />
        <Divider sx={{ opacity: 0.4 }} />
        <ROW label={t("email")}      value={userDetails?.email} />
        <Divider sx={{ opacity: 0.4 }} />
        <ROW label={t("phone")}      value={userDetails?.mobileNumber} />
        <Divider sx={{ opacity: 0.4 }} />
        <ROW label={t("country")}    value={userDetails?.country} />
      </Box>

      {/* call center identity card */}
      <Box
        sx={{
          p: 3, borderRadius: "18px",
          boxShadow: bothFilled
            ? `var(--shadow-card), 0 0 0 2px rgba(34,197,94,0.3)`
            : "var(--shadow-card)",
          transition: "box-shadow 0.25s ease",
          ...glassSx,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
          <Box
            sx={{
              width: 38, height: 38, borderRadius: "11px",
              background: alpha("#06B6D4", 0.1),
              display: "flex", alignItems: "center", justifyContent: "center",
            }}
          >
            <BusinessOutlinedIcon sx={{ fontSize: 20, color: "#06B6D4" }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 600, fontSize: "0.9375rem", color: "text.primary", lineHeight: 1.2 }}>
              {t("callCenterIdentity")}
            </Typography>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              {t("announcedToCallers")}
            </Typography>
          </Box>
          {bothFilled && (
            <Chip
              icon={<CheckCircleIcon sx={{ fontSize: "13px !important", color: "#22C55E !important" }} />}
              label={t("complete")} size="small"
              sx={{
                ml: "auto", fontWeight: 600, fontSize: "0.75rem",
                background: alpha("#22C55E", 0.1), color: "#22C55E",
                border: `1px solid ${alpha("#22C55E", 0.25)}`,
              }}
            />
          )}
        </Box>

        <Divider sx={{ mb: 2.5 }} />

        {SUPPORTED_CALL_LANGS.filter(({ code }) => (profile?.languages || ["en", "ar"]).includes(code)).map(({ code, label, dir }) => {
          const val = profile?.locales?.[code]?.name || "";
          return (
            <Box key={code} sx={{ mb: 2.5 }}>
              <Typography
                variant="caption"
                sx={{ display: "block", mb: 1.5, color: "text.disabled", textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 700, fontSize: "0.6875rem" }}
              >
                {label}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <TextField
                  fullWidth label={t("companyNameIn", { ns: "createplan", language: label })}
                  value={val}
                  onChange={(e) => dispatch(setLocaleName({ lang: code, value: e.target.value }))}
                  onBlur={() => dispatch(saveProfileMeta())}
                  error={!val.trim()}
                  helperText={!val.trim() ? t("enCompanyRequired") : ""}
                  inputProps={{ dir }}
                  size="medium"
                />
                <Tooltip title={t("previewEnPronunciation")} arrow>
                  <IconButton
                    onClick={() => handleTTS(val)}
                    disabled={!val.trim()}
                    sx={{
                      flexShrink: 0, color: "text.secondary",
                      transition: "transform 0.8s ease, color 0.15s",
                      "&:hover": { transform: "rotate(360deg)", color: "primary.main" },
                    }}
                  >
                    <VolumeUpOutlinedIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          );
        })}

        {bothFilled && (
          <Box
            sx={{
              mt: 3, p: 1.75, borderRadius: 2,
              background: alpha("#22C55E", 0.06),
              border: `1px solid ${alpha("#22C55E", 0.18)}`,
              display: "flex", alignItems: "center", gap: 1,
            }}
          >
            <CheckCircleIcon sx={{ fontSize: 16, color: "#22C55E", flexShrink: 0 }} />
            <Typography variant="body2" sx={{ color: "#22C55E", fontWeight: 500, fontSize: "0.8125rem" }}>
              {t("identityComplete")}
            </Typography>
          </Box>
        )}
      </Box>

      {/* ── working hours card ── */}
      <WorkingHoursCard glassSx={{ ...glassSx, boxShadow: "var(--shadow-card)" }} />

      {/* ── language & voice identity card ── */}
      <LanguageIdentityCard glassSx={{ ...glassSx, boxShadow: "var(--shadow-card)" }} />

      {/* ── Arabic dialect & voice selector — only when Arabic is enabled ── */}
      {(profile?.languages || []).includes("ar") && (
        <ArabicDialectCard glassSx={{ ...glassSx, boxShadow: "var(--shadow-card)" }} />
      )}

      {/* ── Voice ID for non-Arabic enabled languages ── */}
      <NonArVoiceCard
        languages={profile?.languages || []}
        glassSx={{ ...glassSx, boxShadow: "var(--shadow-card)" }}
      />

      {/* ── phone numbers card ── */}
      <PhoneNumbersSection />

      {/* ── security / password card ── */}
      <ChangePasswordCard glassSx={{ ...glassSx, boxShadow: "var(--shadow-card)" }} />
    </Box>
  );
};

export default Profile;
